// src/utils/cache/cache-helpers.js
// Shared cache utilities - single source of truth for common cache operations
// Used by: cards-cache.js, spots-cache.js, explore-tags-cache.js, etc.

import { LOGGING } from '/settings.js';

/**
 * Default batch size for API requests
 */
export const DEFAULT_BATCH_SIZE = 50;

/**
 * Creates a batch loader with in-flight request tracking
 * @param {Object} config - Configuration object
 * @param {Map} config.cache - The cache Map to use
 * @param {Map} config.inFlightRequests - The in-flight requests Map
 * @param {Function} config.getCacheKey - Function to get cache key for an item (item) => key
 * @param {Function} config.getBatchKey - Function to create batch key from batch array (batch, ...args) => key
 * @param {Function} config.filterUncached - Function to filter uncached items (items, cache, getCacheKey) => uncachedItems
 * @param {Function} config.fetchBatch - Function to fetch a batch (batch, ...args) => Promise<data>
 * @param {Function} config.processResponse - Function to process response and store in cache (data, batch, cache, getCacheKey, allResults) => void
 * @param {Function} config.getCached - Function to get cached items (items, cache, getCacheKey) => Map
 * @param {Function} config.getDefaultValue - Function to get default value for missing items (item) => defaultValue
 * @param {string} config.cacheName - Name for logging (e.g., "Cards Cache")
 * @param {number} config.maxBatchSize - Maximum batch size (default: 50)
 * @returns {Function} Batch load function
 */
export function createBatchLoader(config) {
    const {
        cache,
        inFlightRequests,
        getCacheKey,
        getBatchKey,
        filterUncached,
        fetchBatch,
        processResponse,
        getCached,
        getDefaultValue,
        cacheName = 'Cache',
        maxBatchSize = DEFAULT_BATCH_SIZE
    } = config;

    return async function batchLoad(items, ...args) {
        if (!items || !Array.isArray(items) || items.length === 0) {
            return new Map();
        }

        // Filter out items that are already cached
        const uncachedItems = filterUncached(items, cache, getCacheKey, ...args);

        if (uncachedItems.length === 0) {
            // All items are already cached
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[${cacheName}] All ${items.length} items already cached`);
            }
            return getCached(items, cache, getCacheKey, ...args);
        }

        // Chunk uncached items into batches
        const batches = [];
        for (let i = 0; i < uncachedItems.length; i += maxBatchSize) {
            batches.push(uncachedItems.slice(i, i + maxBatchSize));
        }

        const allResults = new Map();

        for (const batch of batches) {
            try {
                // Create batch key for in-flight tracking
                const batchKey = getBatchKey(batch, ...args);

                // Check if this batch is already in flight
                if (inFlightRequests.has(batchKey)) {
                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.log(`[${cacheName}] Batch already in flight for ${batch.length} items, waiting...`);
                    }
                    // Wait for the in-flight request to complete
                    await inFlightRequests.get(batchKey);
                    // After it completes, check cache again
                    const cached = getCached(batch, cache, getCacheKey, ...args);
                    cached.forEach((value, key) => {
                        allResults.set(key, value);
                    });
                    continue;
                }

                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[${cacheName}] Batch loading ${batch.length} items:`, batch);
                }

                // Create the fetch promise and store it as in-flight
                const fetchPromise = (async () => {
                    try {
                        const data = await fetchBatch(batch, ...args);
                        // Process response and store in cache
                        processResponse(data, batch, cache, getCacheKey, allResults, getDefaultValue, ...args);
                    } catch (error) {
                        console.error(`[${cacheName}] Error batch loading:`, error);
                        // Cache default values for failed items to prevent retry loops
                        batch.forEach(item => {
                            const key = getCacheKey(item, ...args);
                            if (!cache.has(key)) {
                                const defaultValue = getDefaultValue ? getDefaultValue(item) : null;
                                cache.set(key, defaultValue);
                                allResults.set(key, defaultValue);
                            }
                        });
                    } finally {
                        // Remove from in-flight requests when done
                        inFlightRequests.delete(batchKey);
                    }
                })();

                // Store the promise as in-flight
                inFlightRequests.set(batchKey, fetchPromise);

                // Wait for it to complete
                await fetchPromise;
            } catch (error) {
                console.error(`[${cacheName}] Error in batch processing:`, error);
            }
        }

        // Return all results (cached + newly loaded)
        const finalResults = getCached(items, cache, getCacheKey, ...args);
        // Merge with allResults (newly loaded items take precedence)
        allResults.forEach((value, key) => {
            finalResults.set(key, value);
        });
        return finalResults;
    };
}

/**
 * Creates a simple cache manager with basic operations
 * @param {Object} config - Configuration object
 * @param {Map} config.cache - The cache Map to use
 * @param {Map} config.inFlightRequests - The in-flight requests Map (optional, for simple caches)
 * @param {Function} config.getCacheKey - Function to get cache key (item, ...args) => key
 * @param {string} config.cacheName - Name for logging
 * @returns {Object} Cache manager with get, set, has, clear methods
 */
export function createCacheManager(config) {
    const {
        cache,
        inFlightRequests = null,
        getCacheKey,
        cacheName = 'Cache'
    } = config;

    return {
        /**
         * Get cached value
         * @param {*} item - Item to get cache key for
         * @param {...*} args - Additional arguments for getCacheKey
         * @returns {*} Cached value or undefined
         */
        get(item, ...args) {
            const key = getCacheKey(item, ...args);
            return cache.get(key);
        },

        /**
         * Set cached value
         * @param {*} item - Item to set cache key for
         * @param {*} value - Value to cache
         * @param {...*} args - Additional arguments for getCacheKey
         */
        set(item, value, ...args) {
            const key = getCacheKey(item, ...args);
            cache.set(key, value);
        },

        /**
         * Check if item is cached
         * @param {*} item - Item to check
         * @param {...*} args - Additional arguments for getCacheKey
         * @returns {boolean} True if cached
         */
        has(item, ...args) {
            const key = getCacheKey(item, ...args);
            return cache.has(key);
        },

        /**
         * Clear cache for specific items or all
         * @param {Array|*} items - Items to clear (optional, clears all if not provided)
         * @param {...*} args - Additional arguments for getCacheKey
         */
        clear(items = null, ...args) {
            if (items && Array.isArray(items)) {
                items.forEach(item => {
                    const key = getCacheKey(item, ...args);
                    cache.delete(key);
                });
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[${cacheName}] Cleared cache for ${items.length} items`);
                }
            } else {
                cache.clear();
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[${cacheName}] Cleared all cache`);
                }
            }
        },

        /**
         * Get all cached items
         * @param {Array} items - Items to get (optional, returns all if not provided)
         * @param {...*} args - Additional arguments for getCacheKey
         * @returns {Map} Map of key -> value
         */
        getAll(items = null, ...args) {
            const results = new Map();
            if (items && Array.isArray(items)) {
                items.forEach(item => {
                    const key = getCacheKey(item, ...args);
                    if (cache.has(key)) {
                        results.set(key, cache.get(key));
                    }
                });
            } else {
                cache.forEach((value, key) => {
                    results.set(key, value);
                });
            }
            return results;
        },

        /**
         * Check if request is in flight (only if inFlightRequests is provided)
         * @param {*} item - Item to check
         * @param {...*} args - Additional arguments
         * @returns {boolean} True if in flight
         */
        isInFlight(item, ...args) {
            if (!inFlightRequests) return false;
            // This is a simplified check - actual implementation depends on batch key structure
            // Override in specific cache if needed
            return false;
        }
    };
}

/**
 * Helper to chunk array into batches
 * @param {Array} array - Array to chunk
 * @param {number} size - Batch size
 * @returns {Array<Array>} Array of batches
 */
export function chunkArray(array, size = DEFAULT_BATCH_SIZE) {
    const batches = [];
    for (let i = 0; i < array.length; i += size) {
        batches.push(array.slice(i, i + size));
    }
    return batches;
}

/**
 * Helper to create sorted batch key from array
 * @param {Array} items - Array of items
 * @param {Function} keyFn - Optional function to extract key from item (default: identity)
 * @returns {string} Sorted batch key
 */
export function createSortedBatchKey(items, keyFn = null) {
    const keys = keyFn 
        ? items.map(keyFn).sort((a, b) => a - b)
        : [...items].sort((a, b) => a - b);
    return keys.join(',');
}

